import { dashboardOriginFrom } from '../lib/origin';

// Default icon + bg used when a comment author (legacy data) has no avatar set.
// Backed by migration 0017's BEFORE INSERT trigger — new rows always carry an
// avatar, so this is purely a defensive fallback for older comments.
export const DEFAULT_AVATAR_ID = 'people/49';
export const DEFAULT_AVATAR_BG = '#71717a';

interface AvatarSlotProps {
  apiUrl: string;
  avatarId: string | null;
  avatarBg: string | null;
  authorName: string;
  isOther?: boolean;
  extraClass?: string;
  failedAvatars: Set<string>;
  onAvatarFailed: (slug: string) => void;
}

// Shared avatar-slot renderer for every place that shows a comment author's
// (or the current reviewer's) avatar inside the pin/thread/tooltip. Always
// renders an SVG image — if the chosen slug 404s we swap to a default SVG
// instead of falling back to an initial letter, so the visual language stays
// uniform across the widget.
export function AvatarSlot({
  apiUrl,
  avatarId,
  avatarBg,
  authorName,
  isOther,
  extraClass,
  failedAvatars,
  onAvatarFailed,
}: AvatarSlotProps) {
  const requestedId = avatarId ?? DEFAULT_AVATAR_ID;
  const id = failedAvatars.has(requestedId) ? DEFAULT_AVATAR_ID : requestedId;
  const url = avatarSrc(apiUrl, id);
  const bg = avatarBg ?? DEFAULT_AVATAR_BG;
  const cls = `pin__avatar${isOther ? ' pin__avatar--other' : ''}${extraClass ? ` ${extraClass}` : ''}`;
  return (
    <span class={cls} style={`background: ${bg};`} title={authorName}>
      <img
        src={url}
        alt=""
        onError={() => onAvatarFailed(id)}
      />
    </span>
  );
}

// The avatar API URL is colocated with the dashboard's static /avatars folder.
// We derive it from the api base URL by stripping the trailing '/api' segment.
export function avatarSrc(apiUrl: string, slug: string): string {
  return `${dashboardOriginFrom(apiUrl)}/avatars/${slug}.svg`;
}
